import { createSlice, createSelector } from '@reduxjs/toolkit';
import {
    fetchMultiGensetSummary,
    buildGensetQueryKey,
    fetchGensetSetup,
    fetchGensetStatusHydration,
    fetchGensetStatusRefresh,
    fetchGensetRuntimeCheck,
} from './gensetApi';

const EMPTY_ENTRY = Object.freeze({ loading: false, error: null, data: null, fetchedAt: null });
const EMPTY_FLEET = Object.freeze({});
const EMPTY_VEHICLES = Object.freeze([]);
const DEFAULTS_FALLBACK = Object.freeze({ overuseThresholdMin: 240, tamperThresholdMin: 360, genset_lph: 2 });
const EMPTY_SETUP = Object.freeze({
    loading: false,
    error: null,
    fleet: {},
    vehiclesByImei: {},
    imeis: [],
    fetchedAt: null,
    generatedAt: null,
});
const EMPTY_STATUS = Object.freeze({
    loading: false,
    refreshing: false,
    fastHydrationLoading: false,
    fullHydrationLoading: false,
    hasFullHydrationData: false,
    liveFlowActive: false,
    batchHydrationActive: false,
    hydratedImeisCount: 0,
    expectedImeisCount: 0,
    hydrationComplete: false,
    error: null,
    fleet: {},
    vehiclesByImei: {},
    imeis: [],
    cacheKey: null,
    stateVersion: null,
    delta: false,
    cacheExpired: false,
    fetchedAt: null,
    generatedAt: null,
    lastFastHydrationPayload: null,
    lastFullHydrationPayload: null,
});
const EMPTY_RUNTIME = Object.freeze({
    loading: false,
    error: null,
    fleet: {},
    vehiclesByImei: {},
    imeis: [],
    fetchedAt: null,
    generatedAt: null,
});

const initialState = {
    byKey: {},
    lastKey: null,
    defaults: {
        overuseThresholdMin: 240,
        tamperThresholdMin: 360,
        genset_lph: 2,
    },
    setup: { ...EMPTY_SETUP },
    status: { ...EMPTY_STATUS },
    runtime: { ...EMPTY_RUNTIME },
    refresh: {
        active: false,
        intervalMs: 180000,
        lastTickAt: null,
    },
};

const normalizeImei = (value) => String(value || '').trim();
const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};

const mergeNonEmptyFields = (current = {}, incoming = {}) => {
    const next = { ...current };
    Object.entries(incoming || {}).forEach(([key, value]) => {
        if (isEmptyValue(value)) return;
        next[key] = value;
    });
    return next;
};

const mapVehiclesByImei = (vehicles = []) => {
    const byImei = {};
    const imeis = [];
    (Array.isArray(vehicles) ? vehicles : []).forEach((vehicle) => {
        const imei = normalizeImei(vehicle?.imei || vehicle?.IMEI || vehicle?.assetId);
        if (!imei) return;
        byImei[imei] = vehicle;
        imeis.push(imei);
    });
    return { byImei, imeis };
};

const mergeVehiclesByImei = (current = {}, updates = []) => {
    const next = { ...current };
    (Array.isArray(updates) ? updates : []).forEach((vehicle) => {
        const imei = normalizeImei(vehicle?.imei || vehicle?.IMEI || vehicle?.assetId);
        if (!imei) return;
        const existing = next[imei] || {};
        next[imei] = mergeNonEmptyFields(existing, vehicle || {});
    });
    return next;
};

const mergeVehiclesPreservingFields = (current = {}, updates = [], preserve = []) => {
    const next = { ...current };
    (Array.isArray(updates) ? updates : []).forEach((vehicle) => {
        const imei = normalizeImei(vehicle?.imei || vehicle?.IMEI || vehicle?.assetId);
        if (!imei) return;
        const existing = next[imei] || {};
        const merged = { ...existing, ...vehicle };
        preserve.forEach((field) => {
            if (field === 'currentAddress') {
                const incoming = vehicle?.currentAddress;
                const hasIncoming = incoming && (incoming?.net_addr || incoming?.fmtd_addr);
                if (!hasIncoming && existing?.currentAddress) {
                    merged.currentAddress = existing.currentAddress;
                }
                return;
            }
            if (existing?.[field] !== undefined && existing?.[field] !== null) {
                merged[field] = existing[field];
            }
        });
        next[imei] = merged;
    });
    return next;
};

const mergeFastFleet = (current = {}, incoming = {}) => {
    const next = { ...current, ...(incoming || {}) };
    const preserveKeys = ['totalGensetHoursMinSum', 'totalGensetHoursMinUnion', 'estimatedCost'];
    preserveKeys.forEach((key) => {
        const existing = current?.[key];
        const incomingValue = incoming?.[key];
        if (existing !== undefined && existing !== null) {
            if (incomingValue === undefined || incomingValue === null || incomingValue === 0) {
                next[key] = existing;
            }
        }
    });
    return next;
};

const normalizeHydrationPayload = (payload = {}) => {
    const includePeriods = payload?.includePeriods !== false;
    const includeAddresses = payload?.includeAddresses !== false;
    return { ...payload, includePeriods, includeAddresses };
};

const isAppendHydrationPayload = (payload = {}) => Boolean(payload?.appendCache && payload?.cacheKey);
const toFiniteNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const gensetSlice = createSlice({
    name: 'genset',
    initialState,
    reducers: {
        setDefaults(state, action) {
            const { overuseThresholdMin, tamperThresholdMin, genset_lph } = action.payload || {};
            if (typeof overuseThresholdMin === 'number') state.defaults.overuseThresholdMin = overuseThresholdMin;
            if (typeof tamperThresholdMin === 'number') state.defaults.tamperThresholdMin = tamperThresholdMin;
            if (typeof genset_lph === 'number') state.defaults.genset_lph = genset_lph;
        },
        setRefreshState(state, action) {
            const { active, intervalMs } = action.payload || {};
            if (typeof active === 'boolean') state.refresh.active = active;
            if (typeof intervalMs === 'number') state.refresh.intervalMs = intervalMs;
            if (active === false) state.refresh.lastTickAt = null;
        },
        markRefreshTick(state) {
            state.refresh.lastTickAt = Date.now();
        },
        setLiveFlowState(state, action) {
            const { active } = action.payload || {};
            if (typeof active === 'boolean') {
                state.status.liveFlowActive = active;
            }
        },
        setBatchHydrationState(state, action) {
            const {
                active,
                hydratedImeisCount,
                expectedImeisCount,
                hydrationComplete,
            } = action.payload || {};
            if (typeof active === 'boolean') {
                state.status.batchHydrationActive = active;
                if (active) {
                    state.status.hasFullHydrationData = false;
                }
            }
            const hydratedParsed = toFiniteNumber(hydratedImeisCount);
            if (hydratedParsed !== null) {
                state.status.hydratedImeisCount = hydratedParsed;
            }
            const expectedParsed = toFiniteNumber(expectedImeisCount);
            if (expectedParsed !== null) {
                state.status.expectedImeisCount = expectedParsed;
            }
            if (typeof hydrationComplete === 'boolean') {
                state.status.hydrationComplete = hydrationComplete;
            }
            if (
                !state.status.hydrationComplete
                && state.status.expectedImeisCount > 0
                && state.status.hydratedImeisCount >= state.status.expectedImeisCount
            ) {
                state.status.hydrationComplete = true;
            }
            if (state.status.hydrationComplete) {
                state.status.hasFullHydrationData = true;
            }
        },
        clearGensetStatus(state) {
            state.status = { ...EMPTY_STATUS };
        },
        resetGensetSession(state) {
            state.setup = { ...EMPTY_SETUP };
            state.status = { ...EMPTY_STATUS };
            state.runtime = { ...EMPTY_RUNTIME };
            state.refresh = { ...initialState.refresh };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMultiGensetSummary.pending, (state, action) => {
                const key = buildGensetQueryKey(action.meta?.arg || {});
                if (!state.byKey[key]) state.byKey[key] = { ...EMPTY_ENTRY };
                state.byKey[key].loading = true;
                state.byKey[key].error = null;
            })
            .addCase(fetchMultiGensetSummary.fulfilled, (state, action) => {
                const { data, queryKey } = action.payload || {};
                const key = queryKey || buildGensetQueryKey(action.meta?.arg || {});
                if (!state.byKey[key]) state.byKey[key] = { ...EMPTY_ENTRY };
                state.byKey[key].loading = false;
                state.byKey[key].data = data;
                state.byKey[key].fetchedAt = Date.now();
                state.lastKey = key;
            })
            .addCase(fetchMultiGensetSummary.rejected, (state, action) => {
                const key = buildGensetQueryKey(action.meta?.arg || {});
                if (!state.byKey[key]) state.byKey[key] = { ...EMPTY_ENTRY };
                state.byKey[key].loading = false;
                state.byKey[key].error = action.payload?.error || 'Failed to load';
            })
            .addCase(fetchGensetSetup.pending, (state) => {
                state.setup.loading = true;
                state.setup.error = null;
            })
            .addCase(fetchGensetSetup.fulfilled, (state, action) => {
                const data = action.payload?.data || {};
                const { byImei, imeis } = mapVehiclesByImei(data?.vehicles);
                state.setup.loading = false;
                state.setup.error = null;
                state.setup.fleet = data?.fleet || {};
                state.setup.vehiclesByImei = byImei;
                state.setup.imeis = imeis;
                state.setup.fetchedAt = Date.now();
                state.setup.generatedAt = data?.generatedAt || null;
            })
            .addCase(fetchGensetSetup.rejected, (state, action) => {
                state.setup.loading = false;
                state.setup.error = action.payload?.error || 'Failed to load';
            })
            .addCase(fetchGensetStatusHydration.pending, (state, action) => {
                const normalized = normalizeHydrationPayload(action.meta?.arg || {});
                const isFull = normalized.includePeriods && normalized.includeAddresses;
                const isAppend = isAppendHydrationPayload(normalized);
                if (isFull) {
                    state.status.lastFullHydrationPayload = normalized;
                } else {
                    state.status.lastFastHydrationPayload = normalized;
                }
                if (isFull) {
                    // Keep panel content visible during append-cache chunk hydration.
                    state.status.fullHydrationLoading = !isAppend;
                } else {
                    state.status.fastHydrationLoading = true;
                    // Full hydration phase starts with explicit full requests after fast response.
                    state.status.fullHydrationLoading = false;
                }
                state.status.loading = true;
                state.status.error = null;
                state.status.cacheExpired = false;
                if (!isFull) {
                    state.status.hydrationComplete = false;
                    state.status.hydratedImeisCount = 0;
                    state.status.expectedImeisCount = 0;
                    state.status.hasFullHydrationData = false;
                }
                if (isFull && isAppend) {
                    state.status.batchHydrationActive = true;
                    state.status.hydrationComplete = false;
                    state.status.hasFullHydrationData = false;
                    const expectedCount = Array.isArray(normalized?.expectedImeis) ? normalized.expectedImeis.length : null;
                    if (expectedCount !== null) {
                        state.status.expectedImeisCount = expectedCount;
                    }
                }
            })
            .addCase(fetchGensetStatusHydration.fulfilled, (state, action) => {
                const normalized = normalizeHydrationPayload(action.meta?.arg || {});
                const isFull = normalized.includePeriods && normalized.includeAddresses;
                const isAppend = isAppendHydrationPayload(normalized);
                if (isFull) {
                    state.status.fullHydrationLoading = false;
                } else {
                    state.status.fastHydrationLoading = false;
                }
                state.status.loading = state.status.fastHydrationLoading || state.status.fullHydrationLoading;
                const data = action.payload?.data || {};
                state.status.error = null;
                if (data?.status === 'cache_expired') {
                    state.status.cacheExpired = true;
                    state.status.cacheKey = null;
                    state.status.stateVersion = null;
                    state.status.batchHydrationActive = false;
                    state.status.hydrationComplete = false;
                    return;
                }
                const hasFull = state.status.hasFullHydrationData;
                if (isFull && isAppend) {
                    if (data?.fleet) {
                        state.status.fleet = data.fleet;
                    }
                    const merged = mergeVehiclesByImei(state.status.vehiclesByImei, data?.vehicles);
                    state.status.vehiclesByImei = merged;
                    state.status.imeis = Object.keys(merged);
                } else if (isFull || !hasFull) {
                    const { byImei, imeis } = mapVehiclesByImei(data?.vehicles);
                    state.status.fleet = data?.fleet || {};
                    state.status.vehiclesByImei = byImei;
                    state.status.imeis = imeis;
                } else {
                    state.status.fleet = mergeFastFleet(state.status.fleet, data?.fleet || {});
                    const merged = mergeVehiclesPreservingFields(
                        state.status.vehiclesByImei,
                        data?.vehicles,
                        ['period', 'kilometersTravelled', 'currentAddress']
                    );
                    state.status.vehiclesByImei = merged;
                    state.status.imeis = Object.keys(merged);
                }
                if (data?.cacheKey) state.status.cacheKey = data.cacheKey;
                if (data?.stateVersion !== undefined) state.status.stateVersion = data.stateVersion;
                state.status.delta = Boolean(data?.delta);
                state.status.cacheExpired = false;
                state.status.fetchedAt = Date.now();
                state.status.generatedAt = data?.generatedAt || null;
                const hydratedCount = toFiniteNumber(data?.hydratedImeisCount);
                const expectedCount = toFiniteNumber(data?.expectedImeisCount);
                if (hydratedCount !== null) state.status.hydratedImeisCount = hydratedCount;
                if (expectedCount !== null) state.status.expectedImeisCount = expectedCount;
                if (typeof data?.hydrationComplete === 'boolean') {
                    state.status.hydrationComplete = data.hydrationComplete;
                }
                if (
                    !state.status.hydrationComplete
                    && state.status.expectedImeisCount > 0
                    && state.status.hydratedImeisCount >= state.status.expectedImeisCount
                ) {
                    state.status.hydrationComplete = true;
                }
                if (isFull && isAppend) {
                    state.status.batchHydrationActive = !state.status.hydrationComplete;
                }
                if (isFull && !isAppend) {
                    state.status.batchHydrationActive = false;
                    state.status.hydrationComplete = true;
                    state.status.hydratedImeisCount = state.status.imeis.length;
                    state.status.expectedImeisCount = state.status.imeis.length;
                }
                if (isFull && state.status.hydrationComplete) {
                    state.status.hasFullHydrationData = true;
                }
            })
            .addCase(fetchGensetStatusHydration.rejected, (state, action) => {
                const normalized = normalizeHydrationPayload(action.meta?.arg || {});
                const isFull = normalized.includePeriods && normalized.includeAddresses;
                const isAppend = isAppendHydrationPayload(normalized);
                if (isFull) {
                    state.status.fullHydrationLoading = false;
                } else {
                    state.status.fastHydrationLoading = false;
                }
                state.status.loading = state.status.fastHydrationLoading || state.status.fullHydrationLoading;
                state.status.error = action.payload?.error || 'Failed to load';
                if (isFull && isAppend) {
                    state.status.batchHydrationActive = false;
                }
            })
            .addCase(fetchGensetStatusRefresh.pending, (state) => {
                state.status.refreshing = true;
                state.status.error = null;
            })
            .addCase(fetchGensetStatusRefresh.fulfilled, (state, action) => {
                const data = action.payload?.data || {};
                state.status.refreshing = false;
                state.status.error = null;
                if (data?.status === 'cache_expired') {
                    state.status.cacheExpired = true;
                    state.status.cacheKey = null;
                    state.status.stateVersion = null;
                    return;
                }
                state.status.cacheExpired = false;
                state.status.delta = Boolean(data?.delta);
                if (data?.fleet) {
                    state.status.fleet = data?.delta
                        ? mergeNonEmptyFields(state.status.fleet, data.fleet)
                        : data.fleet;
                }
                if (data?.stateVersion !== undefined) state.status.stateVersion = data.stateVersion;
                if (data?.delta) {
                    const merged = mergeVehiclesByImei(state.status.vehiclesByImei, data?.vehicles);
                    const removedImeis = Array.isArray(data?.removedImeis) ? data.removedImeis : [];
                    removedImeis.forEach((imei) => {
                        const key = normalizeImei(imei);
                        if (key && merged[key]) delete merged[key];
                    });
                    state.status.vehiclesByImei = merged;
                    state.status.imeis = Object.keys(merged);
                } else {
                    const { byImei, imeis } = mapVehiclesByImei(data?.vehicles);
                    state.status.vehiclesByImei = byImei;
                    state.status.imeis = imeis;
                }
                state.status.fetchedAt = Date.now();
                state.status.generatedAt = data?.generatedAt || null;
            })
            .addCase(fetchGensetStatusRefresh.rejected, (state, action) => {
                state.status.refreshing = false;
                state.status.error = action.payload?.error || 'Failed to load';
            })
            .addCase(fetchGensetRuntimeCheck.pending, (state) => {
                state.runtime.loading = true;
                state.runtime.error = null;
            })
            .addCase(fetchGensetRuntimeCheck.fulfilled, (state, action) => {
                const data = action.payload?.data || {};
                const { byImei, imeis } = mapVehiclesByImei(data?.vehicles);
                state.runtime.loading = false;
                state.runtime.error = null;
                state.runtime.fleet = data?.fleet || {};
                state.runtime.vehiclesByImei = byImei;
                state.runtime.imeis = imeis;
                state.runtime.fetchedAt = Date.now();
                state.runtime.generatedAt = data?.generatedAt || null;
            })
            .addCase(fetchGensetRuntimeCheck.rejected, (state, action) => {
                state.runtime.loading = false;
                state.runtime.error = action.payload?.error || 'Failed to load';
            });
    },
});

export const selectGensetDefaults = (state) => state?.genset?.defaults || DEFAULTS_FALLBACK;
export const selectGensetEntryByKey = (state, key) => state?.genset?.byKey?.[key] || EMPTY_ENTRY;
export const selectLastGensetEntry = (state) => {
    const key = state?.genset?.lastKey;
    if (!key) return EMPTY_ENTRY;
    return state?.genset?.byKey?.[key] || EMPTY_ENTRY;
};
export const selectGensetFleetByKey = (state, key) => {
    const entry = selectGensetEntryByKey(state, key);
    return entry?.data?.fleet || EMPTY_FLEET;
};
export const selectGensetVehiclesByKey = (state, key) => {
    const entry = selectGensetEntryByKey(state, key);
    return entry?.data?.vehicles || EMPTY_VEHICLES;
};

export const selectGensetSetupState = (state) => state?.genset?.setup || EMPTY_SETUP;
export const selectGensetSetupLoading = createSelector(
    [selectGensetSetupState],
    (setup) => Boolean(setup?.loading) || !setup?.fetchedAt
);
export const selectGensetSetupFleet = createSelector(
    [selectGensetSetupState],
    (setup) => setup?.fleet || EMPTY_FLEET
);
export const selectGensetSetupVehiclesByImei = createSelector(
    [selectGensetSetupState],
    (setup) => setup?.vehiclesByImei || {}
);
export const selectGensetSetupVehicles = createSelector(
    [selectGensetSetupState],
    (setup) => (setup?.imeis || []).map((imei) => setup?.vehiclesByImei?.[imei]).filter(Boolean)
);

export const selectGensetStatusState = (state) => state?.genset?.status || EMPTY_STATUS;
export const selectGensetFastHydrationLoading = createSelector(
    [selectGensetStatusState],
    (status) => Boolean(status?.fastHydrationLoading)
);
export const selectGensetFullHydrationLoading = createSelector(
    [selectGensetStatusState],
    (status) => Boolean(status?.fullHydrationLoading)
);
export const selectGensetStatusFleet = createSelector(
    [selectGensetStatusState],
    (status) => status?.fleet || EMPTY_FLEET
);
export const selectGensetStatusVehiclesByImei = createSelector(
    [selectGensetStatusState],
    (status) => status?.vehiclesByImei || {}
);
export const selectGensetStatusVehicles = createSelector(
    [selectGensetStatusState],
    (status) => (status?.imeis || []).map((imei) => status?.vehiclesByImei?.[imei]).filter(Boolean)
);
export const selectGensetRuntimeState = (state) => state?.genset?.runtime || EMPTY_RUNTIME;
export const selectGensetRuntimeLoading = createSelector(
    [selectGensetRuntimeState],
    (runtime) => Boolean(runtime?.loading)
);
export const selectGensetRuntimeFleet = createSelector(
    [selectGensetRuntimeState],
    (runtime) => runtime?.fleet || EMPTY_FLEET
);
export const selectGensetRuntimeVehiclesByImei = createSelector(
    [selectGensetRuntimeState],
    (runtime) => runtime?.vehiclesByImei || {}
);
export const selectGensetRuntimeVehicles = createSelector(
    [selectGensetRuntimeState],
    (runtime) => (runtime?.imeis || []).map((imei) => runtime?.vehiclesByImei?.[imei]).filter(Boolean)
);
export const selectGensetCacheKey = createSelector(
    [selectGensetStatusState],
    (status) => status?.cacheKey || null
);
export const selectGensetStateVersion = createSelector(
    [selectGensetStatusState],
    (status) => status?.stateVersion ?? null
);
export const selectGensetLiveFlowActive = createSelector(
    [selectGensetStatusState],
    (status) => Boolean(status?.liveFlowActive)
);
export const selectGensetBatchHydrationActive = createSelector(
    [selectGensetStatusState],
    (status) => Boolean(status?.batchHydrationActive)
);
export const selectGensetHydrationProgress = createSelector(
    [selectGensetStatusState],
    (status) => ({
        active: Boolean(status?.batchHydrationActive),
        hydratedImeisCount: Number(status?.hydratedImeisCount || 0),
        expectedImeisCount: Number(status?.expectedImeisCount || 0),
        hydrationComplete: Boolean(status?.hydrationComplete),
    })
);
export const selectGensetRefreshState = (state) => state?.genset?.refresh || {};
export const selectGensetRefreshActive = createSelector(
    [selectGensetRefreshState],
    (refresh) => Boolean(refresh?.active)
);

export const selectGensetMergedVehicles = createSelector(
    [selectGensetSetupVehiclesByImei, selectGensetStatusVehiclesByImei, selectGensetRuntimeVehiclesByImei],
    (setupByImei, statusByImei, runtimeByImei) => {
        const merged = {};
        const keys = new Set([
            ...Object.keys(setupByImei || {}),
            ...Object.keys(statusByImei || {}),
            ...Object.keys(runtimeByImei || {}),
        ]);
        keys.forEach((imei) => {
            const setupVehicle = setupByImei?.[imei] || {};
            const statusVehicle = statusByImei?.[imei] || {};
            const runtimeVehicle = runtimeByImei?.[imei] || {};
            const setupIssues = Array.isArray(setupVehicle?.issues) ? setupVehicle.issues : [];
            const runtimeSource = Array.isArray(runtimeVehicle?.issues) ? runtimeVehicle.issues : null;
            const runtimeIssues = runtimeSource ?? (Array.isArray(statusVehicle?.issues) ? statusVehicle.issues : []);
            const combinedMap = new Map();
            const combinedExtras = [];
            setupIssues.forEach((issue) => {
                const code = issue?.code;
                if (code) {
                    if (!combinedMap.has(code)) combinedMap.set(code, issue);
                } else {
                    combinedExtras.push(issue);
                }
            });
            runtimeIssues.forEach((issue) => {
                const code = issue?.code;
                if (code) {
                    combinedMap.set(code, issue);
                } else {
                    combinedExtras.push(issue);
                }
            });
            const combinedIssues = [...combinedMap.values(), ...combinedExtras];
            merged[imei] = {
                ...setupVehicle,
                ...runtimeVehicle,
                ...statusVehicle,
                setupIssues,
                runtimeIssues,
                combinedIssues,
                issues: combinedIssues,
            };
        });
        return Object.values(merged);
    }
);

export const selectGensetTotals = createSelector(
    [selectGensetFleetByKey],
    (fleet) => {
        const f = fleet || {};
        const totalSum = Number(f?.totalGensetHoursMinSum || 0);
        const totalUnion = Number(f?.totalGensetHoursMinUnion || 0);
        const estimatedCost = Number(f?.estimatedCost || 0);
        return { totalSum, totalUnion, estimatedCost };
    }
);

export const {
    setDefaults,
    setRefreshState,
    markRefreshTick,
    setLiveFlowState,
    setBatchHydrationState,
    clearGensetStatus,
    resetGensetSession,
} = gensetSlice.actions;
export default gensetSlice.reducer;
export { gensetSlice };
